import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Menu,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowKeys } from '../../constants/enums';
import {
  selectActiveRestaurantDetails,
  selectRestaurantsById,
} from '../../selectors/restaurant';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    // No margin — the header Toolbar's 4px gap handles spacing uniformly.
    margin: 0,
  },
  buttonLabel: {
    // Show the full restaurant name — never ellipsize. Match the other header
    // text size (0.75rem) so a long name stays on the single header line.
    display: 'inline-block',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  menuItemText: {
    // Dropdown options match the header text size (0.75rem).
    fontSize: '0.75rem',
  },
  dropdownIcon: {
    color: '#e0e0e0',
  },
  menu: {
    '& .MuiMenu-paper': {
      maxWidth: '50vw',
      maxHeight: '230px',
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  search: {
    '&.MuiMenuItem-root': {
      position: 'sticky',
      top: 0,
      background: theme.palette.background.paper,
      zIndex: theme.zIndex.appBar,
      '&.Mui-focusVisible': {
        background: theme.palette.background.paper,
      },
      '&:hover': {
        background: theme.palette.background.paper,
      },
    },
  },
  input: {
    color: '#616161',
  },
  name: {
    margin: '0 auto',
    color: theme.palette.common.white,
  },
}));

type RestaurantPickerProps = {
  handleRestaurantSelect: Function;
};

const RestaurantPicker: FC<RestaurantPickerProps> = ({
  handleRestaurantSelect,
}) => {
  const classes = useStyles();
  // hitl_3_0 only: disable the button ripple. During a restaurant switch the
  // blocking menu load freezes the ripple mid-animation, leaving a stuck
  // "texture" overlay on the button. Legacy keeps the ripple.
  const isHitl3 = useSelector(selectFeatureFlagHitl3);
  const restaurantsById = useSelector(selectRestaurantsById);
  const { restaurantCode, restaurantName } = useSelector(
    selectActiveRestaurantDetails
  );
  const [search, setSearch] = useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const filterRef = React.useRef<HTMLInputElement>();
  const isMenuOpen = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const restaurants = Object.values(restaurantsById);
  const showSearchInput = restaurants.length > 5;
  const filteredRestaurantsEntries =
    showSearchInput && search
      ? restaurants.filter((restaurant) => {
          return restaurant.restaurantName
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        })
      : restaurants;

  const { arrowDown, arrowRight, arrowUp } = ArrowKeys;
  const stopPropagation = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case arrowDown:
      case arrowUp:
      case 'Home':
      case 'End':
        break;
      default:
        e.stopPropagation();
    }
  };

  const moveFocusToInput = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Tab' || e.key === arrowRight) {
      e.stopPropagation();
      e.preventDefault();
      if (filterRef?.current) {
        filterRef.current.focus();
      }
    }
  };

  const selectRestaurant = (restaurantCode: string) => {
    setSearch('');
    handleClose();
    handleRestaurantSelect(restaurantCode);
  };

  return (
    <>
      <Tooltip title={restaurantName || ''} arrow>
        <Button
          disableElevation
          disableRipple={isHitl3}
          disableFocusRipple={isHitl3}
          aria-controls="restaurant-picker"
          aria-haspopup="true"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
          }}
          className={classes.button}
          color="secondary"
          variant="contained"
        >
          <span className={classes.buttonLabel}>
            {restaurantName || 'Choose One'}
          </span>
          <ArrowDropDownIcon />
        </Button>
      </Tooltip>
      <Menu
        id="restaurant-picker"
        className={classes.menu}
        keepMounted
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        variant="menu"
      >
        {showSearchInput && (
          <MenuItem className={classes.search} onKeyDown={moveFocusToInput}>
            <TextField
              fullWidth
              data-testid="restaurant-search"
              className={classes.input}
              inputRef={filterRef}
              value={search}
              onKeyDown={stopPropagation}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
              }}
              InputProps={{
                startAdornment: <SearchIcon />,
              }}
            />
          </MenuItem>
        )}
        {filteredRestaurantsEntries.map((restaurant) => {
          return (
            <MenuItem
              key={restaurant.restaurantCode}
              data-testid="restaurant"
              onClick={() => {
                if (restaurantCode !== restaurant.restaurantCode) {
                  selectRestaurant(restaurant.restaurantCode);
                }
              }}
            >
              <Tooltip title={restaurant.restaurantCode} arrow>
                <Typography className={classes.menuItemText}>
                  {restaurant.restaurantName}
                </Typography>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default RestaurantPicker;
